import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const CreateWave = NiceModal.create(({ ids, whID }) => {
	const schema = useMemo(
		() =>
			yup.object().shape(
				{
					assignedPickerID: yup
						.string()
						.nullable()
						.when(['assignedGroupID', 'autoAssignment'], ([assignedGroupID, autoAssignment], schema) =>
							!assignedGroupID && autoAssignment === false ? schema.required() : schema
						),
					assignedGroupID: yup
						.string()
						.nullable()
						.when(['assignedPickerID', 'autoAssignment'], ([assignedPickerID, autoAssignment], schema) =>
							!assignedPickerID && autoAssignment === false ? schema.required() : schema
						),
					autoAssignment: yup.boolean().required()
				},
				['assignedPickerID', 'assignedGroupID', 'autoAssignment']
			),
		[]
	);

	const [isLoading, setIsLoading] = useState(false);
	const [sortingRequirement, setSortingRequirement] = useState(false);

	const { showSuccess, showError } = useAlert();
	const modal = useModal();
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			assignedPickerID: null,
			assignedGroupID: null,
			autoAssignment: null,
			ids: ids.join(',')
		}
	});

	const { setValue, handleSubmit, watch } = formMethods;

	const handleCloseModal = () => modal.remove();

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const resp = await axios.post(ADVANCE_WMS_MODULE.CREATE_WAVE, {
				ids,
				whId: whID,
				assignedPickerID: data.assignedPickerID,
				assignedGroupID: data.assignedGroupID,
				sortingRequirement: sortingRequirement.toString(),
				AutoAssignment: data.autoAssignment
			});
			dispatch(updateDynamicListsInPage());
			handleCloseModal();
			if (
				resp.data.success &&
				(!resp.data.failedIds ||
					(resp.data.failedIds && Array.isArray(resp.data.failedIds) && resp.data.failedIds.length === 0))
			) {
				showSuccess();
			} else {
				showError({ message: 'errorMsg.cannotCreateWaveForSomeItems' });
			}
		} catch (error) {
			AppLogger.error(error);
			showError({
				header: 'error',
				message: error.response?.data?.code
			});
		} finally {
			setIsLoading(false);
		}
	};

	const defaultColSpan = {
		md: 6
	};

	const [labelAutocomplete, setLabelAutocomplete] = useState(null);

	const pickerAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	const handleAutoAssignment = () => {
		setValue('autoAssignment', true, { shouldValidate: true, shouldDirty: true });
		setLabelAutocomplete('automaticAssignment');
		setValue('assignedGroupID', null, { shouldValidate: true, shouldDirty: true });
		setValue('assignedPickerID', null, { shouldValidate: true, shouldDirty: true });
	};

	const handleGroupChange = (value) => {
		setValue('assignedGroupID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });

		if (value) {
			setValue('autoAssignment', false, { shouldValidate: true, shouldDirty: true });
			setLabelAutocomplete(undefined);
			setValue('assignedPickerID', null, { shouldValidate: true, shouldDirty: true });
		}
	};

	const handlePickerChange = (value) => {
		setValue('assignedPickerID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });

		if (value) {
			setValue('autoAssignment', false, { shouldValidate: true, shouldDirty: true });
			setLabelAutocomplete(undefined);
			setValue('assignedGroupID', null, { shouldValidate: true, shouldDirty: true });
		}
	};

	return (
		<Modal fullwidth maxWidth="small">
			<ModalTitle>
				<Typography variant="p1">createWaveWithSelectedOrders</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="orderIds" isLoading={isLoading} required>
						<StringInput name="ids" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="assignedPicker" isLoading={isLoading}>
						<AutoComplete
							valueRemovable={false}
							autoCompleteName={AUTOCOMPLETE.PICKER_USERS_BY_WAREHOUSE}
							name="assignedPickerID"
							setValue={handlePickerChange}
							columns={pickerAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							filterObject={{
								warehouseID: whID
							}}
							value={watch('assignedPickerID')}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="sortingNeeded" isLoading={isLoading}>
						<SwitchInput
							value={sortingRequirement}
							setValue={() => setSortingRequirement(!sortingRequirement)}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="assignedGroup" isLoading={isLoading}>
						<AutoComplete
							valueRemovable={false}
							autoCompleteName={AUTOCOMPLETE.USER_GROUPS}
							name="assignedGroupID"
							setValue={handleGroupChange}
							columns={[{ accessor: 'name', width: 200 }]}
							selectionLabelAccessor={['name']}
							customValue={labelAutocomplete}
							value={watch('assignedGroupID')}
							filterObject={{
								warehouseID: whID
							}}
							firstItem={{
								action: handleAutoAssignment,
								label: <Typography color="var(--blue)">automaticAssignment</Typography>
							}}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />
				<Button
					loading={isLoading}
					label={'submit'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default CreateWave;
